/**
 * contentValidators/sprVisibleWordChecks.ts
 *
 * SPR "zien-is-doen" observability check (Fase B).
 *
 * Spelling items lose didactic value when the word a learner must spell is
 * already fully visible in the stimulus or in the answer options — the item
 * then tests reading recognition, not spelling. This validator FLAGS such
 * cases as `info` issues (severity low → no gate impact) so the admin impact
 * report can quantify how many SPR questions are "te suf" BEFORE we rewrite
 * any seeds ("observability before enforcement").
 *
 * Two conservative patterns are detected:
 *  1. multiple_choice — the correct option is a single, fully-spelled word
 *     that also appears verbatim in the question text.
 *  2. categorize — on weet/regel (recall) families, the chips being sorted
 *     are fully-spelled words rather than cloze fragments (e.g. `han_`).
 *
 * Scope is deliberately narrow to avoid false positives on klank-categorize
 * tasks where sorting a visible word IS the legitimate exercise.
 */

import type { ValidationIssue } from '../../issueContract'
import { info } from '../../issueContract'
import type { Question } from '../../../../content'
import { STAGE } from './shared'

/** Info-severity for observability findings (no gate impact). */
const INFO_SEVERITY = 10

/** SPR module code as used in the seeds (`"module": "SpellingRegel"`). */
const SPR_MODULE = 'SpellingRegel'

/** Letters that make up a Dutch word (incl. common diacritics + internal hyphen/apostrophe). */
const WORD_CHAR = /[a-zàâäçéèêëîïôöùûüœ'’-]/i

/** True when `s` is a single, fully-spelled word (≥3 alphabetic chars, no spaces). */
function isFullWord(s: string): boolean {
  const t = s.trim()
  if (t.length < 3) return false
  if (/\s/.test(t)) return false
  // Reject cloze fragments / placeholders (han_, wor.., ___).
  if (/[_.…]/.test(t)) return false
  // Must be entirely word-characters.
  return [...t].every((ch) => WORD_CHAR.test(ch))
}

/** Tokenize visible text into a lowercase Set of whole words. */
function wordSet(text: string): Set<string> {
  const out = new Set<string>()
  for (const raw of text.split(/[^a-zàâäçéèêëîïôöùûüœ'’-]+/i)) {
    const w = raw.trim().toLowerCase()
    if (w.length >= 3) out.add(w)
  }
  return out
}

function gatherCategorizeChips(data: Record<string, unknown>): string[] {
  const buckets = [data.items, data.words, data.tokens, data.cards].filter(
    Array.isArray,
  ) as unknown[][]
  const chips: string[] = []
  for (const arr of buckets) {
    for (const entry of arr) {
      if (typeof entry === 'string') {
        chips.push(entry)
      } else if (entry && typeof entry === 'object') {
        const o = entry as Record<string, unknown>
        const label = o.text ?? o.word ?? o.label ?? o.value ?? o.content
        if (typeof label === 'string') chips.push(label)
      }
    }
  }
  return chips
}

export function validateSprVisibleWord(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  if (question.module !== SPR_MODULE) return issues

  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const type = question.questionType
  const data = (question.data ?? {}) as Record<string, unknown>

  // Visible stimulus = question text + any inline data text.
  const stimulusParts = [
    question.question ?? '',
    typeof data.text === 'string' ? data.text : '',
    typeof data.sentence === 'string' ? data.sentence : '',
    typeof data.passage === 'string' ? data.passage : '',
    typeof data.context === 'string' ? data.context : '',
  ]
  const visibleWords = wordSet(stimulusParts.join(' '))

  // ── Pattern 1: MC — correct option is a full word visible in the stimulus ──
  if (type === 'multiple_choice' && Array.isArray(question.options)) {
    const idx = Number(question.correctAnswer)
    const correct = question.options[idx]
    if (typeof correct === 'string' && isFullWord(correct)) {
      if (visibleWords.has(correct.trim().toLowerCase())) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_SPR_WORD_VISIBLE',
            `Correct answer "${correct}" is already spelled out in the question — tests recognition, not spelling.`,
            {
              field: 'options',
              questionId: qId,
              familyId: fId,
              severity: INFO_SEVERITY,
              suggestion:
                'Hide the target word with a cloze gap (e.g. "han_") or switch to fill_in / error_spotting so the learner must produce the spelling.',
            },
          ),
        )
      }
    }
  }

  // ── Pattern 2: categorize on recall (weet/regel) families with full-word chips ──
  if (type === 'categorize') {
    const isRecallFamily =
      /weet/i.test(fId) ||
      /weet/i.test(question.subSkill ?? '') ||
      /weet/i.test(question.skill ?? '')
    if (isRecallFamily) {
      const chips = gatherCategorizeChips(data)
      const fullWordChips = chips.filter(isFullWord)
      if (fullWordChips.length > 0) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_SPR_WORD_VISIBLE',
            `Categorize chips on a spelling-recall family are fully spelled (${fullWordChips
              .slice(0, 3)
              .join(', ')}${
              fullWordChips.length > 3 ? ', …' : ''
            }) — the learner sorts a visible word instead of producing the spelling.`,
            {
              field: 'data.items',
              questionId: qId,
              familyId: fId,
              severity: INFO_SEVERITY,
              suggestion:
                'Use cloze fragments on the chips (e.g. "han_" vs "han_") or a fill_in/error_spotting format so the spelling decision is hidden.',
            },
          ),
        )
      }
    }
  }

  return issues
}
